import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	isLoading?: boolean;
	variant?: 'primary' | 'secondary';
}

const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
	// Raised 3D with a top→bottom gradient overlay for extra dimension
	primary:
		'bg-primary text-primary-foreground ' +
		'[background-image:linear-gradient(to_bottom,hsl(0_0%_100%/10%),hsl(0_0%_0%/6%))] ' +
		'shadow-btn ' +
		'hover:bg-primary/90 hover:shadow-btn-hover hover:-translate-y-px ' +
		'active:shadow-btn-active active:translate-y-0 ' +
		'active:[background-image:linear-gradient(to_bottom,hsl(0_0%_0%/4%),hsl(0_0%_0%/10%))]',
	secondary:
		'bg-secondary text-secondary-foreground shadow-sm ' +
		'hover:bg-secondary/80 hover:shadow-md hover:-translate-y-px ' +
		'active:shadow-inner-sm active:translate-y-0',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ children, isLoading, disabled, variant = 'primary', className = '', ...props },
		ref
	) => {
		return (
			<button
				ref={ref}
				disabled={isLoading || disabled}
				className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 ${VARIANT_CLASSES[variant]} ${className}`}
				{...props}
			>
				{isLoading ? (
					<svg
						className='h-4 w-4 animate-spin'
						xmlns='http://www.w3.org/2000/svg'
						fill='none'
						viewBox='0 0 24 24'
						aria-hidden='true'
					>
						<circle
							className='opacity-25'
							cx='12'
							cy='12'
							r='10'
							stroke='currentColor'
							strokeWidth='4'
						/>
						<path
							className='opacity-75'
							fill='currentColor'
							d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
						/>
					</svg>
				) : null}
				{children}
			</button>
		);
	}
);

Button.displayName = 'Button';

export default Button;
